import React, { useState, useMemo } from 'react';
import { X } from 'lucide-react';
import { Task, TaskUpdate, Category, TimePeriod } from '../../types/roadmap';
import { TIME_PERIOD_CONFIGS, getTimePeriodConfig } from '../../utils/timePeriods';

// Form data type without project_id (added by hook)
type TaskFormData = Omit<import('../../types/roadmap').TaskCreate, 'project_id'>;

interface TaskFormProps {
  task?: Task | null;
  categories: Category[];
  defaults?: { categoryId?: string; timePeriod?: TimePeriod };
  onSave: (data: TaskFormData | TaskUpdate) => void;
  onCancel: () => void;
}

export const TaskForm: React.FC<TaskFormProps> = ({
  task,
  categories,
  defaults = {},
  onSave,
  onCancel,
}) => {
  const [title, setTitle] = useState(task?.title || '');
  const [categoryId, setCategoryId] = useState(task?.category_id || defaults.categoryId || categories[0]?.id || '');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(
    task?.time_period || defaults.timePeriod || 'pi5'
  );

  const isEdit = !!task;

  // Get all available time periods sorted by order
  const availablePeriods = useMemo(() => {
    return Object.values(TIME_PERIOD_CONFIGS)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEdit) {
      const updateData: TaskUpdate = {
        title: title !== task.title ? title : undefined,
        category_id: categoryId !== task.category_id ? categoryId : undefined,
        time_period: timePeriod !== task.time_period ? timePeriod : undefined,
      };
      onSave(updateData);
    } else {
      // Get max display_order for this category/time_period
      // For simplicity, we'll use 0 and let the backend handle it
      const createData: TaskFormData = {
        title,
        category_id: categoryId,
        time_period: timePeriod,
        display_order: 0, // Backend will calculate proper order
      };
      onSave(createData);
    }
  };


  const selectedCategory = categories.find(c => c.id === categoryId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">
            {isEdit ? 'Edit Task' : 'Add New Task'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Task Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Task Description *
            </label>
            <textarea
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter task description..."
              rows={3}
              required
            />
          </div>

          {/* Category Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Category *
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.title}
                </option>
              ))}
            </select>
          </div>

          {/* Time Period Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Time Period *
            </label>
            <div className="grid grid-cols-4 gap-3">
              {availablePeriods.map((period) => {
                const isSelected = timePeriod === period.id;
                const borderColor = period.color.border.replace('border-', 'border-');
                return (
                  <button
                    key={period.id}
                    type="button"
                    onClick={() => setTimePeriod(period.id)}
                    className={`p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                      isSelected
                        ? `${borderColor} bg-gray-50`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`h-3 w-full rounded mb-2 ${borderColor.replace('border-', 'bg-')}`} />
                    <p className="font-semibold text-gray-700">{period.label}</p>
                    {period.isHistorical && (
                      <p className="text-xs text-gray-500 mt-1">Historical</p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Preview */}
          {selectedCategory && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Preview
              </label>
              <div className="border border-gray-300 rounded-lg p-4">
                <div className={`text-xs text-gray-600 mb-2`}>
                  {selectedCategory.title} → {getTimePeriodConfig(timePeriod).label}
                </div>
                <div
                  className={`${getTimePeriodConfig(timePeriod).color.bg} ${getTimePeriodConfig(timePeriod).color.border} border-l-4 rounded p-2 text-xs`}
                >
                  {title || 'Task description will appear here...'}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
            >
              {isEdit ? 'Update Task' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
